import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-neutral-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-neutral-950 text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-10" />
        <div className="relative">
          <div className="flex items-center gap-2 font-display text-2xl font-black tracking-tight">
            <span className="inline-block w-3 h-3 bg-yellow-400" />
            ORBIT<span className="text-neutral-400">.pm</span>
          </div>
        </div>
        <div className="relative space-y-6">
          <div>
            <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">Project OS</div>
            <h1 className="font-display text-4xl lg:text-5xl font-black leading-[0.95]">
              Workflow rules.<br />
              <span className="text-yellow-400">Enforced.</span>
            </h1>
          </div>
          <p className="text-neutral-400 text-sm max-w-sm leading-relaxed">
            ClickUp&apos;s hierarchy. Jira&apos;s discipline. Custom statuses, transition gates,
            and time tracking that actually rolls up.
          </p>
          <div className="grid grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 max-w-sm font-mono-data text-xs">
            <div className="bg-neutral-950 p-3"><div className="text-neutral-500">VIEWS</div><div className="text-white">4</div></div>
            <div className="bg-neutral-950 p-3"><div className="text-neutral-500">RULES</div><div className="text-white">∞</div></div>
            <div className="bg-neutral-950 p-3"><div className="text-neutral-500">DRIFT</div><div className="text-yellow-400">0</div></div>
          </div>
        </div>
        <div className="relative font-mono-data text-[10px] text-neutral-600 uppercase tracking-[0.3em]">
          v0.1 · build_2026.02
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-yellow-400" /> ORBIT
          </div>
          <div className="mb-8">
            <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back.</h2>
            <p className="text-sm text-neutral-500 mt-1">Pick up where the work left off.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-sm h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 rounded-sm h-11"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white font-semibold"
              data-testid="login-submit-button"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-neutral-500">
            New here?{" "}
            <Link to="/register" className="text-neutral-950 font-semibold underline underline-offset-4" data-testid="link-register">
              Create an account
            </Link>
          </div>


        </div>
      </div>
    </div>
  );
}
